import {
  IArticle,
  IExtendedArticleLine,
  IInsuranceContract,
  IQuantityPerUnit,
  ISingleOrderedArticleLine,
  PrescriptionType,
} from '@alberta/konexi-shared';
import { ignore } from 'src/app/common/tracking/tracker';
import { ArticleDto } from 'src/app/shared/models/article/article-dto.model';
import { v4 } from 'uuid';

import { IOrderMaximumQuantity } from '../order/contracts/order-maximum-quantity';
import { calculateRecommendedQuantityForOrder } from '../order/order-functions';
import { IMaxAmountOrderedArticleLine } from './max-amount-ordered-article-line';

export class SingleOrderedArticleLineViewModel implements ISingleOrderedArticleLine, IMaxAmountOrderedArticleLine {
  _id: string;
  salesLineId: string;
  proposedArticleLineId: string;
  articleId: string;
  contractId: string;
  articleCustomerNumber: string;
  packagingUnitId: string;
  unit: string;
  quantity: number;
  @ignore()
  recommendedQuantity: number;
  deliveredQuantity: number;
  bookingCode: string;
  serialNumber: string;
  batchNumber: string;
  storageLocation: string;
  isPrivateSale: boolean;
  timestamp: Date;
  archived: boolean;
  groupOrderPosition: number;
  singleOrderId: string;

  @ignore()
  isNew: boolean;
  @ignore()
  isInSeries: boolean;
  @ignore()
  active: boolean;
  @ignore()
  associatedArticle: IArticle = new ArticleDto();
  @ignore()
  notInIntegratedCare: boolean;
  @ignore()
  article: IArticle = new ArticleDto();
  @ignore()
  contract: IInsuranceContract;

  @ignore()
  packagingUnit: IQuantityPerUnit;
  @ignore()
  maxAmount: IOrderMaximumQuantity;

  constructor() {
    this._id = v4();
    this.timestamp = new Date();
    this.archived = false;
  }

  public initialize(
    integratedCareArticleLine: IExtendedArticleLine,
    article: ArticleDto,
    scope: number,
    associatedContract?: IInsuranceContract,
    maxQuantity?: IOrderMaximumQuantity
  ) {
    if (!article) {
      return false;
    }

    this.articleId = integratedCareArticleLine.articleId;
    this.isInSeries = !!integratedCareArticleLine.seriesDataId;
    this.article = article;
    this.associatedArticle = article;
    this.articleCustomerNumber = article.customerNumber;

    if (associatedContract) {
      this.contractId = associatedContract._id;
      this.contract = associatedContract;
    }

    if (this.isInSeries || (article.prescriptionType && article.prescriptionType === PrescriptionType.AidsForUse)) {
      this.active = false;
    } else {
      this.active = true;
    }
    if (!article.packagingUnits || !article.packagingUnits.length) {
      return false;
    }

    this.packagingUnit = article.packagingUnits.find(
      packagingUnit => packagingUnit._id === integratedCareArticleLine.dosage?.packagingId && !packagingUnit.archived
    );

    if (!this.packagingUnit) {
      return false;
    }
    this.unit = this.packagingUnit.unit;
    this.packagingUnitId = this.packagingUnit._id;

    if (!integratedCareArticleLine.dosage?.duration) {
      return false;
    }
    const quantity = calculateRecommendedQuantityForOrder(this.packagingUnit, integratedCareArticleLine, scope);
    this.quantity = quantity;
    this.recommendedQuantity = quantity;
    if (maxQuantity) {
      this.maxAmount = maxQuantity;
      this.maxAmount.currentQuantity += this.quantity * this.packagingUnit.quantity;
    }
    if (integratedCareArticleLine.isPrivateSale) {
      this.isPrivateSale = integratedCareArticleLine.isPrivateSale;
    }

    return true;
  }

  public update(proposedArticleLine: IExtendedArticleLine, scope: number) {
    if (!proposedArticleLine) {
      this.notInIntegratedCare = true;
      this.recommendedQuantity = this.quantity;
      this.isInSeries = false;
      return;
    }

    this.isInSeries = !!proposedArticleLine.seriesDataId;

    if (this.article?.packagingUnits && this.article.packagingUnits.length) {
      const packagingUnit = this.article.packagingUnits.find(pu => pu._id === this.packagingUnitId && !pu.archived);
      if (!packagingUnit) {
        return null;
      }
      this.recommendedQuantity = calculateRecommendedQuantityForOrder(packagingUnit, proposedArticleLine, scope);
    }
  }
}
